import {
  applyDiscountResponseSchema,
  billingCycleSchema,
  cancelPendingDowngradeResponseSchema,
  discountTypeSchema,
  downgradePreviewResponseSchema,
  downgradeSubscriptionResponseSchema,
  extendTrialResponseSchema,
  paymentMethodSchema,
  removeDiscountResponseSchema,
  saleOriginSchema,
  subscriptionOriginSchema,
  subscriptionStatusSchema,
  transferSubscriptionResponseSchema,
  courtesyUpgradeResponseSchema,
  upgradePreviewResponseSchema,
  upgradeSubscriptionResponseSchema,
  type ApplyDiscountDto,
  type ApplyDiscountResponse,
  type CancelPendingDowngradeResponse,
  type CancelSubscriptionDto,
  type CreateStandaloneSubscriptionDto,
  type CreateSubscriptionDto,
  type DowngradePreviewResponse,
  type DowngradeSubscriptionDto,
  type DowngradeSubscriptionResponse,
  type ExtendTrialDto,
  type ExtendTrialResponse,
  type QuerySubscriptionsDto,
  type ReactivateSubscriptionDto,
  type RemoveDiscountResponse,
  type SuspendSubscriptionDto,
  type TransferSubscriptionDto,
  type TransferSubscriptionResponse,
  type UpdateSubscriptionDto,
  type UpgradePreviewResponse,
  type CourtesyUpgradeDto,
  type CourtesyUpgradeResponse,
  type UpgradeSubscriptionDto,
  type UpgradeSubscriptionResponse,
} from '@control-center/shared';
import { z } from 'zod';

import { apiClient } from './api-client';

// =============================================================================
// Response schemas — Story 3.1 (Tier 2 Zod parse obrigatório)
// =============================================================================

const planLiteSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
});

const planDetailedSchema = planLiteSchema.extend({
  type: z.string(),
  monthlyPrice: z.string(),
  annualPrice: z.string().nullable(),
  billingCycle: billingCycleSchema,
});

const clientLiteSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

const clientDetailedSchema = clientLiteSchema.extend({
  email: z.string().nullable(),
  document: z.string().nullable(),
  status: z.string(),
});

const subscriptionLiteSchema = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  planId: z.string().uuid().nullable(),
  // Story 4.13 — recorrente standalone (sem plano) traz a descrição livre.
  description: z.string().nullable().optional(),
  origin: subscriptionOriginSchema,
  price: z.string(),
  status: subscriptionStatusSchema,
  startDate: z.string(),
  endDate: z.string().nullable(),
  billingDay: z.number().nullable(),
  nextBillingDate: z.string().nullable(),
  paymentMethod: paymentMethodSchema.nullable(),
  autoRenew: z.boolean(),
  trialStartsAt: z.string().nullable(),
  trialEndsAt: z.string().nullable(),
  saleOrigin: saleOriginSchema.nullable(),
  soldByUserId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  client: clientLiteSchema,
  plan: planLiteSchema.nullable(),
  _count: z.object({ entitlements: z.number() }),
});
export type SubscriptionLite = z.infer<typeof subscriptionLiteSchema>;

const entitlementSchema = z.object({
  id: z.string().uuid(),
  saasProductId: z.string().uuid(),
  externalPlanSlug: z.string(),
  externalPlanName: z.string(),
  limitsSnapshot: z.unknown(),
  isCustom: z.boolean(),
  provisioningStatus: z.string(),
  saasProduct: z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
  }),
});

// Story 3.5 — Pending downgrade quando agendado
const pendingPlanChangeDetailSchema = z
  .object({
    targetPlanId: z.string().uuid(),
    // `courtesy_end` precisa estar aqui: sem ele o parse da página INTEIRA
    // falha quando a assinatura tem cortesia ativa, e o detalhe não abre.
    changeType: z.enum(['downgrade', 'migration', 'courtesy_end']),
    restorePrice: z.string().optional(),
    scheduledFor: z.string(),
    scheduledAt: z.string(),
    scheduledByUserId: z.string().uuid().nullable(),
  })
  .nullable();

const subscriptionDetailSchema = subscriptionLiteSchema
  .omit({ client: true, plan: true, _count: true })
  .extend({
    client: clientDetailedSchema,
    plan: planDetailedSchema.nullable(),
    entitlements: z.array(entitlementSchema),
    pendingPlanChange: pendingPlanChangeDetailSchema.optional(),
    // Story 3.8 — desconto manual
    discountAmount: z.string().nullable().optional(),
    discountType: discountTypeSchema.nullable().optional(),
    discountReason: z.string().nullable().optional(),
    discountUntil: z.string().nullable().optional(),
    discountMax: z.string().nullable().optional(),
    discountAppliedAt: z.string().nullable().optional(),
    // Story 3.9 — trial
    trialConvertedAt: z.string().nullable().optional(),
    trialExtendedCount: z.number().int().nonnegative().optional(),
    trialExtendedHistory: z.array(z.unknown()).optional(),
  });
export type SubscriptionDetail = z.infer<typeof subscriptionDetailSchema>;

const listResponseSchema = z.object({
  items: z.array(subscriptionLiteSchema),
  meta: z.object({
    next_cursor: z.string().nullable(),
    total_estimate: z.number().nullable(),
  }),
});

const singleResponseSchema = z.object({ data: subscriptionDetailSchema });

export interface SubscriptionListResponse {
  items: SubscriptionLite[];
  meta: { next_cursor: string | null; total_estimate: number | null };
}

export async function fetchSubscriptions(
  params: QuerySubscriptionsDto = { limit: 50 },
): Promise<SubscriptionListResponse> {
  const { data } = await apiClient.get('/api/subscriptions', { params });
  return listResponseSchema.parse(data);
}

export async function fetchSubscription(id: string): Promise<SubscriptionDetail> {
  const { data } = await apiClient.get(`/api/subscriptions/${id}`);
  return singleResponseSchema.parse(data).data;
}

export async function createSubscription(
  payload: CreateSubscriptionDto,
): Promise<SubscriptionDetail> {
  const { data } = await apiClient.post('/api/subscriptions', payload);
  return singleResponseSchema.parse(data).data;
}

/**
 * Story 4.13 — cria assinatura standalone (cobrança recorrente sem plano).
 * Consumido pela tela /charges/new no modo "Recorrente".
 */
export async function createStandaloneSubscription(
  payload: CreateStandaloneSubscriptionDto,
): Promise<SubscriptionDetail> {
  const { data } = await apiClient.post('/api/subscriptions/standalone', payload);
  return singleResponseSchema.parse(data).data;
}

export async function updateSubscription(
  id: string,
  payload: UpdateSubscriptionDto,
): Promise<SubscriptionDetail> {
  const { data } = await apiClient.patch(`/api/subscriptions/${id}`, payload);
  return singleResponseSchema.parse(data).data;
}

// =============================================================================
// Cancel / Reactivate — Story 3.6 (Tier 2 Zod parse)
// =============================================================================

export async function cancelSubscription(
  id: string,
  payload: CancelSubscriptionDto,
): Promise<SubscriptionDetail> {
  const { data } = await apiClient.post(`/api/subscriptions/${id}/cancel`, payload);
  return singleResponseSchema.parse(data).data;
}

export async function revertPendingCancellation(id: string): Promise<SubscriptionDetail> {
  const { data } = await apiClient.delete(`/api/subscriptions/${id}/pending-cancellation`);
  return singleResponseSchema.parse(data).data;
}

export async function reactivateSubscription(
  id: string,
  payload: ReactivateSubscriptionDto = {},
): Promise<SubscriptionDetail> {
  const { data } = await apiClient.post(`/api/subscriptions/${id}/reactivate`, payload);
  return singleResponseSchema.parse(data).data;
}

// =============================================================================
// Suspend — Story 3.3
// =============================================================================

export async function suspendSubscription(
  id: string,
  payload: SuspendSubscriptionDto,
): Promise<SubscriptionDetail> {
  const { data } = await apiClient.post(`/api/subscriptions/${id}/suspend`, payload);
  return singleResponseSchema.parse(data).data;
}

// =============================================================================
// Upgrade — Story 3.4
// =============================================================================

export async function previewSubscriptionUpgrade(
  id: string,
  payload: UpgradeSubscriptionDto,
): Promise<UpgradePreviewResponse['data']> {
  const { data } = await apiClient.post(`/api/subscriptions/${id}/upgrade-preview`, payload);
  return upgradePreviewResponseSchema.parse(data).data;
}

export async function upgradeSubscription(
  id: string,
  payload: UpgradeSubscriptionDto,
): Promise<UpgradeSubscriptionResponse['data']> {
  const { data } = await apiClient.post(`/api/subscriptions/${id}/upgrade`, payload);
  return upgradeSubscriptionResponseSchema.parse(data).data;
}

/**
 * Cortesia: sobe o plano SEM cobrar e SEM alterar o preço do cliente, com volta
 * automática na próxima cobrança. Não tem preview porque não há valor a
 * antecipar — o custo pro cliente é zero por definição.
 */
export async function courtesyUpgradeSubscription(
  id: string,
  payload: CourtesyUpgradeDto,
): Promise<CourtesyUpgradeResponse['data']> {
  const { data } = await apiClient.post(`/api/subscriptions/${id}/courtesy-upgrade`, payload);
  return courtesyUpgradeResponseSchema.parse(data).data;
}

// =============================================================================
// Downgrade — Story 3.5
// =============================================================================

export async function previewSubscriptionDowngrade(
  id: string,
  payload: DowngradeSubscriptionDto,
): Promise<DowngradePreviewResponse['data']> {
  const { data } = await apiClient.post(`/api/subscriptions/${id}/downgrade-preview`, payload);
  return downgradePreviewResponseSchema.parse(data).data;
}

export async function scheduleSubscriptionDowngrade(
  id: string,
  payload: DowngradeSubscriptionDto,
): Promise<DowngradeSubscriptionResponse['data']> {
  const { data } = await apiClient.post(`/api/subscriptions/${id}/downgrade`, payload);
  return downgradeSubscriptionResponseSchema.parse(data).data;
}

export async function cancelPendingDowngrade(
  id: string,
): Promise<CancelPendingDowngradeResponse['data']> {
  const { data } = await apiClient.delete(`/api/subscriptions/${id}/pending-plan-change-downgrade`);
  return cancelPendingDowngradeResponseSchema.parse(data).data;
}

// =============================================================================
// Transfer — Story 3.7
// =============================================================================

export async function transferSubscription(
  id: string,
  payload: TransferSubscriptionDto,
): Promise<TransferSubscriptionResponse['data']> {
  const { data } = await apiClient.post(`/api/subscriptions/${id}/transfer`, payload);
  return transferSubscriptionResponseSchema.parse(data).data;
}

// =============================================================================
// Discount — Story 3.8
// =============================================================================

export async function applySubscriptionDiscount(
  id: string,
  payload: ApplyDiscountDto,
): Promise<ApplyDiscountResponse['data']> {
  const { data } = await apiClient.post(`/api/subscriptions/${id}/discount`, payload);
  return applyDiscountResponseSchema.parse(data).data;
}

/**
 * Pula UMA cobrança: adia o próximo vencimento em 1 mês, no dia combinado.
 *
 * Devolve `applied: false` com motivo quando não dá — assinatura sem cobrança
 * gerenciada, status não elegível, ou mais de uma assinatura cobrável (aí o
 * sistema recusa em vez de escolher sozinho de quem é a cortesia).
 */
export async function skipSubscriptionBilling(
  id: string,
  reason?: string,
): Promise<{
  applied: boolean;
  reason?: string;
  previousNextBillingDate?: string;
  newNextBillingDate?: string;
}> {
  const { data } = await apiClient.post(`/api/subscriptions/${id}/skip-billing`, { reason });
  return data.data;
}

export async function removeSubscriptionDiscount(
  id: string,
): Promise<RemoveDiscountResponse['data']> {
  const { data } = await apiClient.delete(`/api/subscriptions/${id}/discount`);
  return removeDiscountResponseSchema.parse(data).data;
}

// =============================================================================
// Trial — Story 3.9
// =============================================================================

export async function extendSubscriptionTrial(
  id: string,
  payload: ExtendTrialDto,
): Promise<ExtendTrialResponse['data']> {
  const { data } = await apiClient.post(`/api/subscriptions/${id}/extend-trial`, payload);
  return extendTrialResponseSchema.parse(data).data;
}
